import { useState, useEffect, useCallback } from 'react';
import { useSocket } from './useSocket';
import { showBrowserNotification } from '../utils/browserNotifications';
import { requestNotificationPermission, isNotificationPermissionGranted } from '../utils/notificationPermission';

const STORAGE_KEY = 'tb_notifications';
const MAX_NOTIFICATIONS = 50;

export const useNotifications = (userEmail, notificationsEnabled = true) => {
  // Only connect socket if notifications are enabled
  const { socket, connected } = useSocket(notificationsEnabled ? userEmail : null);
  const [notifications, setNotifications] = useState(() => {
    // Load from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
      return [];
    }
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Request notification permission on mount (only if notifications are enabled)
  useEffect(() => {
    if (notificationsEnabled && userEmail && !isNotificationPermissionGranted()) {
      requestNotificationPermission();
    }
  }, [userEmail, notificationsEnabled]);

  // Listen for socket notifications (only if notifications are enabled)
  useEffect(() => {
    if (!notificationsEnabled || !socket || !connected) return;

    const handleNotification = (notificationData) => {
      const notification = {
        ...notificationData,
        id: `${notificationData.type}-${notificationData.requestId}-${Date.now()}`,
        read: false,
        createdAt: notificationData.timestamp || new Date().toISOString()
      };

      setNotifications(prev => {
        const updated = [notification, ...prev].slice(0, MAX_NOTIFICATIONS);
        // Save to localStorage
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (error) {
          console.error('Error saving notifications to storage:', error);
        }
        return updated;
      });

      // Show browser notification (only if enabled)
      if (notificationsEnabled) {
        showBrowserNotification(notificationData);
      }

      // Update unread count
      setUnreadCount(prev => prev + 1);
    };

    socket.on('notification', handleNotification);

    return () => {
      socket.off('notification', handleNotification);
    };
  }, [socket, connected, notificationsEnabled]);

  // Calculate unread count
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving notifications to storage:', error);
      }
      return updated;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving notifications to storage:', error);
      }
      return updated;
    });
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing notifications from storage:', error);
    }
  }, []);

  const getUnreadCount = useCallback(() => {
    return unreadCount;
  }, [unreadCount]);

  const getNotifications = useCallback((limit = null) => {
    if (limit) {
      return notifications.slice(0, limit);
    }
    return notifications;
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    connected,
    markAsRead,
    markAllAsRead,
    clearAll,
    getUnreadCount,
    getNotifications
  };
};

